import React, { createContext, useContext } from 'react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export const UT_FLAG = 'ut-redesign';

interface UtFlag {
  /** True when the Ultimate Team redesign should render. */
  enabled: boolean;
  loading: boolean;
}

const UtFlagContext = createContext<UtFlag | null>(null);

/**
 * Reads the `ut-redesign` flag once for the whole layout. useFeatureFlag runs
 * its own query per call, so pages read this context instead of calling it.
 */
export const UtFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isEnabled, loading } = useFeatureFlag(UT_FLAG);
  const forced = devOverride();
  const value = forced === null ? { enabled: isEnabled, loading } : { enabled: forced, loading: false };
  return <UtFlagContext.Provider value={value}>{children}</UtFlagContext.Provider>;
};

const DEV_OVERRIDE_KEY = 'wnf-ut-override';

/**
 * Dev builds only: `?ut=1` / `?ut=0` forces the redesign on or off (remembered
 * in localStorage; `?ut=clear` forgets it), so screens can be checked locally
 * without editing the shared feature_flags table. Always null in production.
 */
function devOverride(): boolean | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('ut');
    if (q === 'clear') window.localStorage.removeItem(DEV_OVERRIDE_KEY);
    else if (q === '1' || q === '0') window.localStorage.setItem(DEV_OVERRIDE_KEY, q);
    const v = window.localStorage.getItem(DEV_OVERRIDE_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

/**
 * Whether to render the UT view. Outside the provider (e.g. /standalone-stats)
 * it reports disabled, so the classic UI is the safe default.
 */
export const useUt = (): UtFlag => useContext(UtFlagContext) ?? { enabled: false, loading: false };
